import * as React from "react";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { extend, Extent, createEmpty } from "ol/extent";
import { OSM, Vector as VectorSource } from "ol/source";
import { Tile as TileLayer, Image as ImageLayer, Vector as VectorLayer, Layer } from "ol/layer";
import { Feature, View, Map } from "ol";
import { defaults as defaultControls } from "ol/control";
import { FeatureType, RouteLayers, RoutePointDataSource, SelectedStatus } from "../enums";
import { getMiniRouteSymbol, getMiniSymbol } from "./miniSymbols";
import { IMiniMap, OnMiniFeatureClickCallback } from "./miniMapProvider";
import { config } from "../config";
import { ILayerFeatures } from "../mapProvider";

const center = config.defaults.center;

let onMiniFeatureClick: MutableRefObject<OnMiniFeatureClickCallback>;

export const useMiniMap = (target: MutableRefObject<HTMLDivElement>, props: IMiniMap): Map => {
    const [featuresByLayers, setFeaturesByLayers] = useState<ILayerFeatures[]>();
    const _miniMap = useRef<Map>(null);

    onMiniFeatureClick = useRef<OnMiniFeatureClickCallback>(props.onClickOnMiniFeature);
    onMiniFeatureClick.current = useMemo(() => props.onClickOnMiniFeature, [props.onClickOnMiniFeature]);

    useEffect(() => {
        if (!target.current) {
            return;
        }

        const osmLayer = new TileLayer({
            source: new OSM()
        });

        _miniMap.current = new Map({
            layers: [osmLayer],
            target: target.current,
            view: new View({
                projection: "EPSG:3857",
                center,
                zoom: 4
            }),
            controls: defaultControls({ zoom: false, rotate: false, attribution: false })
        });

        _miniMap.current.on("click", (evt) => {
            if (evt.dragging) {
                return;
            }
            _miniMap.current.forEachFeatureAtPixel(evt.pixel, handleObjectClick, {
                layerFilter: (layer) =>
                    getId(layer.get("id")) === RouteLayers.RoutePoints ||
                    getId(layer.get("id")) === config.defaults.clusterLayerId
            });
        });

        const handleObjectClick = (feature: Feature, layer) =>
            onMiniFeatureClick.current?.({
                layerId: getId(layer.get("id")),
                objectId: feature.get("OBJECTID")
            });
    }, []);

    useEffect(() => {
        if (!target.current || !featuresByLayers || !featuresByLayers.length) {
            return;
        }
        featuresByLayers.map((layerFeatures) => {
            const id = layerFeatures.layerId;
            const features = layerFeatures.features.filter((f) => f && f.clone());

            const style =
                id === RouteLayers.Route
                    ? (f) => getMiniRouteSymbol(id as RouteLayers, SelectedStatus.Selected)
                    : id === RouteLayers.RoutePoints
                    ? (f) => getMiniRouteSymbol(id as RouteLayers, SelectedStatus.Default, f.get("TYPE"), 1)
                    : id === RouteLayers.Other || id === RouteLayers.RouteTrack
                    ? (f) => getMiniRouteSymbol(id as RouteLayers, SelectedStatus.Selected, f.get("TYPE"), 1)
                    : (f) => getMiniSymbol(f.get("PLACESUBTYPE"), f.get("VISITED"), 1, f.get("SELECTED"));

            const routeLayer = new VectorLayer({
                id: miniId(id),
                source: new VectorSource({ features }),
                style
            });

            id === RouteLayers.RouteTrack
                ? _miniMap.current.getLayers().insertAt(3, routeLayer)
                : id === RouteLayers.RoutePoints
                ? _miniMap.current.getLayers().insertAt(4, routeLayer)
                : _miniMap.current.addLayer(routeLayer);
        });

        zoomToRoute();
    }, [featuresByLayers]);

    useEffect(() => {
        if (!props.routeId) {
            return;
        }

        const routeMainFeature = props.routeLayers
            .filter((features) => features.some((f) => f.get("FEATURETYPE") === FeatureType.RouteMainPoint))[0]
            .find((item: Feature) => item.get("ROUTEID") === props.routeId);

        routeMainFeature &&
            setFeaturesByLayers([
                { layerId: RouteLayers.Route, features: [routeMainFeature.clone()] },
                {
                    layerId: RouteLayers.RouteTrack,
                    features: cloneRouteFeatures(FeatureType.RouteTrack, props.routeId)
                },
                {
                    layerId: RouteLayers.RoutePoints,
                    features: cloneRouteFeatures(FeatureType.RoutePoint, props.routeId)
                },
                {
                    layerId: RouteLayers.Other,
                    features: cloneRouteFeatures(FeatureType.RouteOtherPlace, props.routeId)
                },
                { layerId: config.defaults.clusterLayerId, features: getLVMFeaturesForRoute(props.routeId) }
            ]);
    }, [props.routeId]);

    const cloneRouteFeatures = useCallback(
        (layerId: string, routeId) => {
            return (
                props.routeLayers &&
                props.routeLayers
                    .filter((features) => features.some((f) => f.get("FEATURETYPE") === layerId))[0]
                    .filter((feature: Feature) => feature.get("ROUTEID") === routeId)
                    .map((f: Feature) => f && f.clone())
            );
        },
        [props.routeLayers]
    );

    const getLVMFeaturesForRoute = useCallback(
        (routeId) => {
            const lvmFeatures = [];
            cloneRouteFeatures(FeatureType.RoutePoint, routeId).map((feature: Feature) => {
                feature.get("TYPE") === RoutePointDataSource.LVM &&
                    feature.get("SOURCEID") &&
                    lvmFeatures.push(props.allFeatures.find((f) => f.get("OBJECTID") === feature.get("SOURCEID")));
            });

            return lvmFeatures.map((f) => f && f.clone());
        },
        [props.allFeatures]
    );

    const zoomToRoute = () => {
        const trackExtent = createEmpty();
        getLayerById(miniId(RouteLayers.RouteTrack))
            .getSource()
            .forEachFeature((feature: Feature) => {
                extend(trackExtent, feature.getGeometry().getExtent());
            });
        zoomToExtent(trackExtent);
    };

    const getLayerById = (id: string): Layer =>
        _miniMap.current
            .getLayers()
            .getArray()
            .find((layer: Layer) => layer.get("id") === id);

    const zoomToExtent = (extent: Extent, callback?: CallableFunction) => {
        _miniMap.current.getView().fit(extent, { padding: [10, 10, 10, 10], callback });
    };
};

const miniId = (id: string) => "mini-" + id;
const getId = (miniId: string) => (miniId ? miniId.slice(5) : miniId);
